/**
 * The meeting-notes card: the marker on the write-up row, and the reader for the file it renders.
 *
 * A video meeting Maestro sat in becomes a conversation whose first row is the write-up.
 * The harness stamps `metadata.meetingNotesCard` on that row with the facts the card's HEADER needs
 * (the meeting's own start and end, the link, who was in the room, who spoke), and attaches the
 * write-up itself as `notes.md` in the Meetings space. The card is a rendering of THAT FILE: the
 * reader edits it, ticks its `- [ ]` lines, and the card re-renders from it. The row's own markdown is
 * only how the file looked when the meeting ended, and the row is never rewritten.
 *
 * Two pure functions with no imports, so both phones can port them line for line and the three
 * surfaces agree on what a line means (`parseMeetingNotes` / `toggleActionItem` on iOS and Android):
 *
 * - `parseMeetingNotes` reads the file's SHAPE, never its words. The headings are localized (English
 *   and Hebrew today), so a section is recognised by what its lines look like — checkbox bullets are
 *   action items, `- **Who:** text` bullets are updates, a paragraph is the TLDR — and by its position
 *   relative to the others. Anything the parser cannot place lands in `rest`, verbatim, for the card
 *   to render as plain markdown after the sections. Nothing is ever dropped.
 * - One hidden line names what the harness wrote, `[//]: # (symphony-notes: draft)` or
 *   `[//]: # (symphony-notes: final topics decisions …)`: a link reference definition, which every
 *   markdown renderer here draws as nothing. It sits after the metadata bullets, where an older parser
 *   puts it in `rest` and leaves every section as it was. `final` lists the kind of each `##` section in
 *   file order, because the final notes can hold three plain lists that no shape tells apart; when the
 *   sections no longer fit the list (the reader edited the file), the shape rules read it instead.
 * - `toggleActionItem` flips ONE checkbox line and returns the whole file with every other byte as it
 *   was. It never rewrites a line it did not recognise.
 *
 * Mirrors iOS `MeetingNotesCard` / Android `MeetingNotesCard`. The marker itself is read by common-lib's
 * `readMeetingNotesCard` (wired into `lib/chat-cards.ts`); its type is restated here, client-local like
 * `lib/workspaces.ts`, so this module stays free of runtime imports and unit-testable on its own.
 */

/** The card marker's shape — common-lib's `MeetingNotesCardMetadata`, minus the fields the card does not draw. */
export interface MeetingNotesCardData {
  meetingSessionId: string;
  /** The meeting's own start and end, epoch ms — from the transcript's first and last stamps. */
  startedAt?: number;
  endedAt?: number;
  meetingUrl?: string;
  /** Display names people chose in the room: text, never links, never resolved against contacts. */
  participants: { name: string; isHost: boolean }[];
  speakers: { name: string; isAgent: boolean }[];
}

// ---- The file ------------------------------------------------------------------------------------

export interface MeetingNotesActionItem {
  /** 0-based line in the file — what `toggleActionItem` takes. */
  lineIndex: number;
  text: string;
  done: boolean;
  owner?: string;
  due?: string;
  /** Set once Maestro linked a task back on this line: ` [task](…/task/<id>)`. */
  taskId?: string;
}

export interface MeetingNotesUpdate {
  who: string;
  text: string;
}

export interface MeetingNotesTopic {
  title: string;
  text: string;
}

export interface ParsedMeetingNotes {
  /** The `#` title, or '' when the file has none. */
  title: string;
  /** The paragraph under the title, before the metadata bullets. */
  lead: string;
  /** The notes are a draft from live audio, and final notes are coming. The lead still opens with the draft's own line. */
  draft: boolean;
  /** The `- **Label:** value` bullets under the lead. Identified by SHAPE — the labels are localized. */
  meta: { date?: string; meetingUrl?: string; attending?: string; spoke?: string };
  tldr: string;
  updates: MeetingNotesUpdate[];
  topics: MeetingNotesTopic[];
  decisions: string[];
  actionItems: MeetingNotesActionItem[];
  openQuestions: string[];
  /** Every block the parser could not place, in file order, headings included. Rendered as markdown. */
  rest: string;
}

/** True when the parser found at least one thing to draw as a SECTION — the card's body. */
export function hasMeetingNotesSections(notes: ParsedMeetingNotes): boolean {
  return (
    Boolean(notes.tldr) ||
    notes.updates.length > 0 ||
    notes.topics.length > 0 ||
    notes.decisions.length > 0 ||
    notes.actionItems.length > 0 ||
    notes.openQuestions.length > 0
  );
}

/**
 * True when the card can draw this file as a card at all: it has sections, or it has the template's
 * metadata bullets. False means the file is prose the template does not describe, and the card renders
 * it as plain markdown instead.
 *
 * Deliberately WIDER than {@link hasMeetingNotesSections}. A meeting nobody spoke in produces a file
 * with a title, a lead and the four bullets and no sections at all — a perfectly drawable card. Judging
 * it by sections alone dropped it to raw markdown AND left the header's fallback to the file's own date
 * and link unable to run, so the facts were printed once as bullets and the grid above them stood empty.
 */
export function hasMeetingNotesShape(notes: ParsedMeetingNotes): boolean {
  return hasMeetingNotesSections(notes) || Object.keys(notes.meta).length > 0;
}

const CHECKBOX_LINE = /^(\s*[-*+]\s+\[)( |x|X)(\]\s?)(.*)$/;
const BULLET_LINE = /^\s*[-*+]\s+(.*)$/;
const BOLD_LABEL_BULLET = /^\s*[-*+]\s+\*\*([^*]+?):?\*\*:?\s*(.*)$/;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
/** The task link Maestro appends: `[task](https://symphony.wix.app/task/<id>)` or `[task](symphony://task/<id>)`. */
const TASK_LINK = /\s*\[[^\]]*\]\([^)\s]*\/task\/([A-Za-z0-9_-]+)[^)]*\)\s*$/;
/** The trailing `(owner: X, due: Y)` — the labels are localized, so only the SHAPE is matched: a
 *  parenthetical of `label: value` pairs at the very end of the line. */
const TRAILING_META = /\s*\(([^()]*:[^()]*)\)\s*$/;
const URL_VALUE = /^https?:\/\/\S+$/i;
const NOTES_MARKER = /^\s{0,3}\[\/\/\]:\s*#\s*\(symphony-notes:\s*([^)]*?)\s*\)\s*$/;
/** The shape each kind in a `final` marker must have, or the marker no longer describes the file. */
const FINAL_KIND_SHAPE: Record<string, SectionKind> = {
  topics: 'labelled',
  decisions: 'plain',
  'action-items': 'checkbox',
  'open-questions': 'plain',
  'also-in-room': 'plain',
};
/** A value that reads as a date: a four-digit year, or a clock, anywhere in it. */
const DATE_LIKE = /\b\d{4}\b|\b\d{1,2}:\d{2}\b/;

interface Section {
  heading: string;
  headingLine: number;
  lines: string[];
  firstLine: number;
}

/**
 * Read `notes.md` into the card's sections. Pure; the same input always gives the same output, and
 * every line the parser did not recognise is still in `rest`.
 */
export function parseMeetingNotes(markdown: string): ParsedMeetingNotes {
  const lines = markdown.split('\n');
  const result: ParsedMeetingNotes = {
    title: '',
    lead: '',
    draft: false,
    meta: {},
    tldr: '',
    updates: [],
    topics: [],
    decisions: [],
    actionItems: [],
    openQuestions: [],
    rest: '',
  };
  const rest: string[] = [];

  // Split into the preamble (before the first `##`) and the `##` sections.
  let i = 0;
  const preamble: string[] = [];
  while (i < lines.length && !isSectionHeading(lines[i])) {
    preamble.push(lines[i]);
    i++;
  }
  const sections: Section[] = [];
  while (i < lines.length) {
    const heading = HEADING.exec(lines[i]);
    const section: Section = { heading: heading ? heading[2].trim() : '', headingLine: i, lines: [], firstLine: i + 1 };
    i++;
    while (i < lines.length && !isSectionHeading(lines[i])) {
      section.lines.push(lines[i]);
      i++;
    }
    sections.push(section);
  }

  // --- The marker: read from the preamble, and never shown.
  let marker: string[] = [];
  for (let m = preamble.length - 1; m >= 0; m--) {
    const found = NOTES_MARKER.exec(preamble[m]);
    if (found) {
      if (marker.length === 0) marker = found[1].split(/\s+/).filter(Boolean);
      preamble.splice(m, 1);
    }
  }
  result.draft = marker[0] === 'draft';

  // --- Preamble: title, lead, metadata bullets. Anything else goes to `rest`.
  {
    let p = 0;
    while (p < preamble.length && !preamble[p].trim()) p++;
    const title = p < preamble.length ? HEADING.exec(preamble[p]) : null;
    if (title && title[1].length === 1) {
      result.title = title[2].trim();
      p++;
    }
    const paragraph: string[] = [];
    const metaBullets: string[] = [];
    const unplaced: string[] = [];
    for (; p < preamble.length; p++) {
      const line = preamble[p];
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (BOLD_LABEL_BULLET.test(line)) {
        metaBullets.push(line);
      } else if (BULLET_LINE.test(line) || HEADING.test(line) || metaBullets.length > 0) {
        unplaced.push(line);
      } else {
        paragraph.push(trimmed);
      }
    }
    result.lead = paragraph.join(' ');
    result.meta = classifyMeta(metaBullets.map((line) => BOLD_LABEL_BULLET.exec(line)![2].trim()));
    if (unplaced.length) rest.push(unplaced.join('\n'));
  }

  const kinds = sections.map(classifySection);

  // --- Final notes: each section takes the kind the marker names, while every one still fits it.
  const named = marker[0] === 'final' ? marker.slice(1) : [];
  if (named.length > 0 && named.length === sections.length && named.every((kind, idx) => FINAL_KIND_SHAPE[kind] === kinds[idx])) {
    sections.forEach((section, idx) => {
      const kind = named[idx];
      const bullets = section.lines.flatMap((line) => {
        const m = BULLET_LINE.exec(line);
        return m && m[1].trim() ? [m[1].trim()] : [];
      });
      if (kind === 'topics') {
        for (const line of section.lines) {
          const m = BOLD_LABEL_BULLET.exec(line);
          if (m) result.topics.push({ title: m[1].trim(), text: m[2].trim() });
        }
      } else if (kind === 'decisions') {
        result.decisions.push(...bullets);
      } else if (kind === 'action-items') {
        section.lines.forEach((line, offset) => {
          const item = parseActionItem(line, section.firstLine + offset);
          if (item) result.actionItems.push(item);
        });
      } else if (kind === 'open-questions') {
        result.openQuestions.push(...bullets);
      } else {
        rest.push([lines[section.headingLine], ...section.lines].join('\n').replace(/\n+$/, ''));
      }
    });
    result.rest = rest.filter((block) => block.trim()).join('\n\n');
    return result;
  }

  // --- Sections, by shape and position.
  const actionIndex = kinds.indexOf('checkbox');
  let tldrTaken = false;
  let updatesTaken = false;
  let actionsTaken = false;
  let questionsTaken = false;
  // Plain-bullet sections: the one right after the action items is Open questions; without an
  // action-items section, the LAST of two or more is. A lone plain list with nothing to place it
  // against stays in `rest`, heading and all, rather than being guessed at.
  const plainIndexes = kinds.flatMap((k, idx) => (k === 'plain' ? [idx] : []));
  const questionsIndex =
    actionIndex >= 0 ? plainIndexes.find((idx) => idx > actionIndex) ?? -1 : plainIndexes.length >= 2 ? plainIndexes[plainIndexes.length - 1] : -1;

  sections.forEach((section, idx) => {
    const kind = kinds[idx];
    if (kind === 'paragraph' && !tldrTaken) {
      tldrTaken = true;
      result.tldr = section.lines
        .map((l) => l.trim())
        .filter(Boolean)
        .join(' ');
      return;
    }
    if (kind === 'labelled' && !updatesTaken) {
      updatesTaken = true;
      for (const line of section.lines) {
        const m = BOLD_LABEL_BULLET.exec(line);
        if (m) result.updates.push({ who: m[1].trim(), text: m[2].trim() });
      }
      return;
    }
    if (kind === 'checkbox' && !actionsTaken) {
      actionsTaken = true;
      section.lines.forEach((line, offset) => {
        const item = parseActionItem(line, section.firstLine + offset);
        if (item) result.actionItems.push(item);
      });
      return;
    }
    if (idx === questionsIndex && !questionsTaken) {
      questionsTaken = true;
      for (const line of section.lines) {
        const m = BULLET_LINE.exec(line);
        if (m && m[1].trim()) result.openQuestions.push(m[1].trim());
      }
      return;
    }
    rest.push([lines[section.headingLine], ...section.lines].join('\n').replace(/\n+$/, ''));
  });

  result.rest = rest.filter((block) => block.trim()).join('\n\n');
  return result;
}

function isSectionHeading(line: string): boolean {
  const m = HEADING.exec(line);
  return !!m && m[1].length >= 2;
}

type SectionKind = 'paragraph' | 'labelled' | 'checkbox' | 'plain' | 'empty' | 'mixed';

/** What a section's non-empty lines look like. A section is one kind only when EVERY bullet in it agrees;
 *  a section that mixes shapes is `mixed` and lands in `rest` whole, so nothing in it is half-read. */
function classifySection(section: Section): SectionKind {
  const body = section.lines.filter((l) => l.trim());
  if (body.length === 0) return 'empty';
  const bullets = body.filter((l) => BULLET_LINE.test(l));
  if (bullets.length === 0) return 'paragraph';
  if (bullets.length !== body.length) return 'mixed';
  if (bullets.every((l) => CHECKBOX_LINE.test(l))) return 'checkbox';
  if (bullets.every((l) => BOLD_LABEL_BULLET.test(l))) return 'labelled';
  if (bullets.some((l) => CHECKBOX_LINE.test(l) || BOLD_LABEL_BULLET.test(l))) return 'mixed';
  return 'plain';
}

/** The four metadata bullets, told apart by their VALUES: the link is a URL, the date has a year or a
 *  clock in it, and the two lists of names come in the template's order — attending, then spoke. */
function classifyMeta(values: string[]): ParsedMeetingNotes['meta'] {
  const meta: ParsedMeetingNotes['meta'] = {};
  const names: string[] = [];
  for (const value of values) {
    if (!value) continue;
    if (!meta.meetingUrl && URL_VALUE.test(value)) meta.meetingUrl = value;
    else if (!meta.date && DATE_LIKE.test(value)) meta.date = value;
    else names.push(value);
  }
  if (names[0]) meta.attending = names[0];
  if (names[1]) meta.spoke = names[1];
  return meta;
}

/** One `- [ ] text (owner: X, due: Y) [task](…)` line, or null for a line that is not a checkbox. */
export function parseActionItem(line: string, lineIndex: number): MeetingNotesActionItem | null {
  const m = CHECKBOX_LINE.exec(line);
  if (!m) return null;
  let text = m[4].trim();
  let taskId: string | undefined;
  const link = TASK_LINK.exec(text);
  if (link) {
    taskId = link[1];
    text = text.slice(0, link.index).trim();
  }
  let owner: string | undefined;
  let due: string | undefined;
  const trailing = TRAILING_META.exec(text);
  if (trailing) {
    const pairs = trailing[1]
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean);
    const values = pairs.map((pair) => {
      const colon = pair.indexOf(':');
      return colon >= 0 ? pair.slice(colon + 1).trim() : pair;
    });
    if (pairs.every((pair) => pair.includes(':'))) {
      owner = values[0] || undefined;
      due = values[1] || undefined;
      text = text.slice(0, trailing.index).trim();
    }
  }
  const item: MeetingNotesActionItem = { lineIndex, text, done: m[2].toLowerCase() === 'x' };
  if (owner) item.owner = owner;
  if (due) item.due = due;
  if (taskId) item.taskId = taskId;
  return item;
}

/**
 * Flip the checkbox on line `lineIndex` and return the whole file. Every other byte — including the
 * line's own text, its owner, its task link and a `\r` line ending — is kept exactly. A line that is
 * not a checkbox returns the input unchanged, so a stale index can never damage the file.
 */
export function toggleActionItem(markdown: string, lineIndex: number): string {
  const lines = markdown.split('\n');
  if (lineIndex < 0 || lineIndex >= lines.length) return markdown;
  const line = lines[lineIndex];
  const cr = line.endsWith('\r');
  const bare = cr ? line.slice(0, -1) : line;
  const m = CHECKBOX_LINE.exec(bare);
  if (!m) return markdown;
  const flipped = m[2] === ' ' ? 'x' : ' ';
  lines[lineIndex] = `${m[1]}${flipped}${m[3]}${m[4]}${cr ? '\r' : ''}`;
  return lines.join('\n');
}

/** The done / total count the agent-page row and the card header read. */
export function countActionItems(items: { done: boolean }[]): { total: number; done: number } {
  return { total: items.length, done: items.filter((item) => item.done).length };
}

/**
 * The meeting link when it is a web URL, else undefined. The link is metadata the calendar and the room
 * supplied and the card renders it as a raw `<a href>`, so this is a closed allowlist narrower than
 * `lib/safe-url.ts`: a meeting link is `http(s)` or it is nothing — a `javascript:` or `data:` scheme is
 * dropped, not shown. Same rule on both phones.
 */
export function meetingLinkUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}
